import type { Aircraft, Booking } from "../lib/types";
import { evaluateWarnings } from "../lib/warnings";
import { lessonById, profileById, aircraftById } from "../lib/lookups";
import { statusLabel } from "../lib/status";
import { timeToMin } from "../lib/time";
import type { BoardStore } from "../stores/board";

const START_HOUR = 6;
const END_HOUR = 21;
const DEFAULT_DURATION_MIN = 60;
const SNAP_MIN = 15;

function currentMin(): number {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}

function clampToWindow(m: number, start: number, end: number): number {
  return Math.max(start, Math.min(end, m));
}

interface UIStoreShape {
  openNew(prefill?: { start?: string; end?: string; aircraftId?: string | null }): void;
  openView(b: Booking): void;
}

function boardStore(): BoardStore {
  return (window as unknown as { Alpine: { store(name: string): BoardStore } }).Alpine.store("board");
}
function uiStore(): UIStoreShape {
  return (window as unknown as { Alpine: { store(name: string): UIStoreShape } }).Alpine.store("ui");
}

function snap(m: number): number {
  return Math.round(m / SNAP_MIN) * SNAP_MIN;
}

function clampMin(m: number): number {
  return Math.max(START_HOUR * 60, Math.min(END_HOUR * 60, m));
}

function minToTime(m: number): string {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

function canCreate(): boolean {
  const r = boardStore().role;
  return r === "Owner" || r === "Scheduler";
}

export function scheduleAlpine() {
  const startMin = START_HOUR * 60;
  const endMin = END_HOUR * 60;
  const totalMin = endMin - startMin;

  const pos = (t: string) => ((timeToMin(t) - startMin) / totalMin) * 100;
  const widthFor = (s: string, e: string) => ((timeToMin(e) - timeToMin(s)) / totalMin) * 100;

  function pxToMin(clientX: number, rect: DOMRect): number {
    const pct = (clientX - rect.left) / rect.width;
    const m = startMin + pct * totalMin;
    return clampMin(snap(m));
  }

  return {
    nowMin: clampToWindow(currentMin(), startMin, endMin),
    get nowPct(): number { return ((this.nowMin - startMin) / totalMin) * 100; },
    get nowLabel(): string { return minToTime(this.nowMin); },
    init(this: { nowMin: number }) {
      const tick = () => { this.nowMin = clampToWindow(currentMin(), startMin, endMin); };
      tick();
      setInterval(tick, 30_000);
    },
    pos,
    w: widthFor,

    ghostRow: null as string | null,
    ghostStartMin: 0,
    ghostEndMin: 0,
    dragMoved: false,

    get aircraftRows(): Aircraft[] {
      const board = boardStore();
      return board.lookups.aircraft.filter(
        (a) => board.filterAircraft === "all" || a.id === board.filterAircraft,
      );
    },

    visibleBookings(): Booking[] {
      const board = boardStore();
      return board.bookings.filter((b) => {
        if (board.filterAircraft !== "all" && b.aircraftId !== board.filterAircraft) return false;
        if (board.filterInstructor !== "all" && b.instructorId !== board.filterInstructor) return false;
        return true;
      });
    },

    bookingsFor(aircraftId: string): Booking[] {
      return this.visibleBookings().filter((b) => b.aircraftId === aircraftId);
    },

    classroomBookings(): Booking[] {
      return this.visibleBookings().filter((b) => !b.aircraftId);
    },

    get showClassroom(): boolean {
      return this.classroomBookings().length > 0;
    },

    blockHasWarn(b: Booking): boolean {
      if (b.status === "canceled" || b.status === "completed") return false;
      const board = boardStore();
      return evaluateWarnings(b, board.bookings, {
        aircraft: board.lookups.aircraft,
        certs: board.lookups.certs,
        availability: board.lookups.availability,
        profiles: board.lookups.profiles,
      }).length > 0;
    },

    warnCount(b: Booking): string {
      const board = boardStore();
      const n = evaluateWarnings(b, board.bookings, {
        aircraft: board.lookups.aircraft,
        certs: board.lookups.certs,
        availability: board.lookups.availability,
        profiles: board.lookups.profiles,
      }).length;
      return `${n} warning${n > 1 ? "s" : ""}`;
    },

    lessonName(id: string): string {
      return lessonById(boardStore().lookups.lessons, id)?.name ?? "—";
    },

    studentLabel(id: string | null): string {
      const u = profileById(boardStore().lookups.profiles, id);
      return u ? u.name : "—";
    },

    aircraftLabel(id: string | null): string {
      if (!id) return "classroom";
      return aircraftById(boardStore().lookups.aircraft, id)?.tail ?? "—";
    },

    statusLabel(b: Booking): string {
      return statusLabel(b.status);
    },

    metaLine(b: Booking): string {
      const profiles = boardStore().lookups.profiles;
      const stu = profileById(profiles, b.studentId);
      const inst = profileById(profiles, b.instructorId);
      const stuShort = stu
        ? stu.name.split(" ").map((p) => p[0]).join("") + " " + stu.name.split(" ")[1]
        : "—";
      const instShort = inst ? " · " + inst.name.split(" ").map((p) => p[0]).join("") : "";
      return stuShort + instShort;
    },

    userChipHTML(id: string | null): string {
      const u = profileById(boardStore().lookups.profiles, id);
      if (!u) return '<span class="dim">—</span>';
      return `<span class="user-chip">
        <span class="avatar" style="background: ${u.color}22; color: ${u.color}">${u.initials}</span>
        <span>${u.name}</span>
      </span>`;
    },

    isGhostFor(aircraftId: string | null): boolean {
      if (this.ghostRow === null) return false;
      const a = aircraftId ?? "__classroom__";
      return this.ghostRow === a;
    },

    ghostLeftPct(): number {
      return ((this.ghostStartMin - startMin) / totalMin) * 100;
    },

    ghostWidthPct(): number {
      return ((this.ghostEndMin - this.ghostStartMin) / totalMin) * 100;
    },

    ghostLabel(): string {
      return `${minToTime(this.ghostStartMin)}–${minToTime(this.ghostEndMin)}`;
    },

    onPointerDown(event: PointerEvent, aircraftId: string | null) {
      if (!canCreate()) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      const target = event.target as HTMLElement;
      if (target.closest(".tl-block")) return;
      const grid = event.currentTarget as HTMLElement;
      const rect = grid.getBoundingClientRect();
      const m = pxToMin(event.clientX, rect);
      this.ghostRow = aircraftId ?? "__classroom__";
      this.ghostStartMin = m;
      this.ghostEndMin = m + DEFAULT_DURATION_MIN;
      this.dragMoved = false;
      grid.setPointerCapture(event.pointerId);
      event.preventDefault();
    },

    onPointerMove(event: PointerEvent) {
      if (this.ghostRow === null) return;
      const grid = event.currentTarget as HTMLElement;
      if (!grid.hasPointerCapture(event.pointerId)) return;
      const rect = grid.getBoundingClientRect();
      const m = pxToMin(event.clientX, rect);
      const min = Math.max(this.ghostStartMin + SNAP_MIN, m);
      if (min !== this.ghostEndMin) {
        this.ghostEndMin = min;
        this.dragMoved = true;
      }
    },

    onPointerUp(event: PointerEvent, aircraftId: string | null) {
      if (this.ghostRow === null) return;
      const grid = event.currentTarget as HTMLElement;
      if (grid.hasPointerCapture(event.pointerId)) grid.releasePointerCapture(event.pointerId);
      const start = minToTime(this.ghostStartMin);
      const end = minToTime(Math.max(this.ghostStartMin + 30, this.ghostEndMin));
      this.ghostRow = null;
      this.dragMoved = false;
      uiStore().openNew({ start, end, aircraftId: aircraftId ?? null });
    },

    onPointerCancel() {
      this.ghostRow = null;
      this.dragMoved = false;
    },

    onContextMenu(event: MouseEvent, aircraftId: string | null) {
      if (!canCreate()) return;
      event.preventDefault();
      const target = event.target as HTMLElement;
      if (target.closest(".tl-block")) return;
      const grid = event.currentTarget as HTMLElement;
      const rect = grid.getBoundingClientRect();
      const m = pxToMin(event.clientX, rect);
      const start = minToTime(m);
      const end = minToTime(m + DEFAULT_DURATION_MIN);
      uiStore().openNew({ start, end, aircraftId: aircraftId ?? null });
    },
  };
}
